import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import {
    IThirdPartyContractHold,
    IThirdPartyLeaseDuplicateCluster,
    IThirdPartyLeaseListItem,
    recordingKeyLabel,
    recordingLabel,
} from 'src/models/thirdPartyLease/ThirdPartyLease';

export interface IDuplicateClusterCardProps {
    cluster: IThirdPartyLeaseDuplicateCluster;
    onOpenContract?: (agreementType: string, contractId: number) => void;
}

const formatDate = (value?: string) => (value ? moment(value).format('MM/DD/YYYY') : 'N/A');

const formatInterest = (value?: number) =>
    value === undefined || value === null ? '—' : Number(value).toFixed(8).replace(/0+$/, '').replace(/\.$/, '.0');

const formatAcres = (value?: number) =>
    value === undefined || value === null ? '—' : Number(value).toFixed(4).replace(/\.?0+$/, '');

const countyLabel = (countyName?: string, stateAbbreviation?: string) => {
    if (!countyName) {
        return undefined;
    }

    return stateAbbreviation ? `${countyName}, ${stateAbbreviation}` : countyName;
};

/* Flattens contracts to one row per hold so the reviewer sees every place the contract attaches,
   not a rolled-up count. Contracts in these clusters almost always have exactly one hold - that is
   the bug being hunted - so this is usually one row per contract anyway. */
interface IHoldRow {
    contract: IThirdPartyLeaseListItem;
    hold?: IThirdPartyContractHold;
    isFirstOfContract: boolean;
}

const DuplicateClusterCard = (props: IDuplicateClusterCardProps) => {
    const { cluster } = props;
    const [isOpen, setIsOpen] = useState(false);

    const tierClass = cluster.matchTier === 'High' ? 'tier-high' : 'tier-low';

    const tierExplanation =
        cluster.matchTier === 'High'
            ? 'Same county, recording info, lessor and contract date.'
            : 'No recording info to match on - grouped by lessor, lessee and contract date.';

    const rows = useMemo<Array<IHoldRow>>(() => {
        const flattened: Array<IHoldRow> = [];

        (cluster.contracts || []).forEach((contract) => {
            if (!contract.holds || contract.holds.length === 0) {
                flattened.push({ contract, hold: undefined, isFirstOfContract: true });
                return;
            }

            contract.holds.forEach((hold, index) => {
                flattened.push({ contract, hold, isFirstOfContract: index === 0 });
            });
        });

        return flattened;
    }, [cluster.contracts]);

    const owners = useMemo(() => _.uniq(rows.map((r) => r.hold?.ownerName).filter((o) => !!o)), [rows]);

    /* County is not part of the cluster key - too many contracts have none for that to work - so a
       cluster can in principle span counties. Report them from the holds and shout if there is more
       than one, since a book and page only identifies a document within a single county. */
    const counties = useMemo(
        () =>
            _.uniq(
                rows
                    .map((r) => countyLabel(r.hold?.countyName, r.hold?.stateAbbreviation))
                    .filter((c) => c !== undefined)
            ),
        [rows]
    );

    return (
        <div className="tpl-cluster">
            <div className="tpl-cluster-header" onClick={() => setIsOpen(!isOpen)} title={tierExplanation}>
                <i className={isOpen ? 'far fa-chevron-down' : 'far fa-chevron-right'} />

                <span className={`tpl-tier ${tierClass}`}>{cluster.matchTier}</span>

                <span className="tpl-type-badge">{cluster.agreementType}</span>

                <span className="tpl-cluster-title">
                    {recordingKeyLabel(cluster.recordingKey)}
                    {' — '}
                    {cluster.originalLessor || 'Unknown lessor'}
                    {' — '}
                    {formatDate(cluster.contractDate)}
                </span>

                <span className="tpl-cluster-meta">
                    <strong>{cluster.contractCount}</strong> contract records
                    {' · '}
                    {cluster.holdCount} holds across {cluster.tractCount} tracts
                    {counties.length === 1 && ` · ${counties[0]}`}
                    {counties.length > 1 && ` · ${counties.length} counties`}
                </span>
            </div>

            {counties.length > 1 && (
                <div className="tpl-county-warning">
                    <i className="fas fa-exclamation-circle" />{' '}
                    These records span {counties.length} counties ({counties.join(', ')}). A book and page is only
                    unique within a county, so this is likely a coincidence rather than a duplicate — check before
                    merging.
                </div>
            )}

            {cluster.siblingLessorCount > 0 && (
                <div className="tpl-sibling-warning">
                    <i className="fas fa-exclamation-triangle" />{' '}
                    This document also covers {cluster.siblingLessorCount} other lessor
                    {cluster.siblingLessorCount === 1 ? '' : 's'}. Whether those belong on the same contract record is a
                    judgment call — this group covers only {cluster.originalLessor || 'this lessor'}.
                </div>
            )}

            {isOpen && (
                <div>
                    {owners.length > 1 && (
                        <div className="tpl-cluster-note">
                            One contract attached across {owners.length} mineral owners — the lessor is the same on
                            every record, so these are the same document entered per owner/tract.
                        </div>
                    )}

                    <div className="tpl-row tpl-head">
                        <div className="tpl-col-agrmt">Agreement #</div>
                        <div className="tpl-col-recinfo">Vol/Page | Inst#</div>
                        <div className="tpl-col-global">Tract / Horizon</div>
                        <div className="tpl-col-owner">Mineral Owner</div>
                        <div className="tpl-col-num">Gross Ac</div>
                        <div className="tpl-col-num">Net Ac</div>
                        <div className="tpl-col-num">Interest</div>
                    </div>

                    {rows.map((row, index) => (
                        <div
                            key={`${row.contract.agreementType}-${row.contract.contractId}-${row.hold?.holdId ?? index}`}
                            className="tpl-row clickable"
                            onClick={() =>
                                props.onOpenContract &&
                                props.onOpenContract(row.contract.agreementType, row.contract.contractId)
                            }
                        >
                            <div
                                className="tpl-col-agrmt"
                                title={`Internal id ${row.contract.contractId}`}
                            >
                                {row.isFirstOfContract ? row.contract.agreementNumber || '—' : ''}
                            </div>
                            <div className="tpl-col-recinfo">
                                {row.isFirstOfContract ? recordingLabel(row.contract) : ''}
                            </div>
                            <div className="tpl-col-global" title={row.hold?.globalId}>
                                {row.hold?.shortGlobalId || '—'}
                            </div>
                            <div className="tpl-col-owner">{row.hold?.ownerName || '—'}</div>
                            <div className="tpl-col-num">{formatAcres(row.hold?.grossAcres)}</div>
                            <div className="tpl-col-num">{formatAcres(row.hold?.netAcres)}</div>
                            <div className="tpl-col-num">{formatInterest(row.hold?.decimalInterest)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DuplicateClusterCard;



// WEBPACK FOOTER //
// ./src/components/project/sections/ThirdPartyLeases/DuplicateClusterCard.tsx